import { ref, watch, onMounted, computed, onUnmounted } from 'vue';
import useAvailabilityStore from '~/stores/useAvailabilityStore';
import { injectLocal, provideLocal } from '@vueuse/core';
import {
  navigationStoreChangeListener,
  pageStoreChangeListener,
  removeStoreChangeListener,
  STORE_CHANGED_ON_HEADER,
  STORE_CHANGED_ON_PAGE,
} from '@nuxt-common/locator/app/utils/globalStoreChange';
import { Cookie } from '@vue-util/cookie';
import useMembersOfProduct from '~/composables/useMembersOfProduct';
import { getBigTicketZipCookie, getSelectedStoreCookie } from '~/utils/cookiesUtils';
import { usePageData } from '~/composables/usePageData';
import { cookies } from 'config';
import { getStoreLocation } from '../utils/localStorage';
import { FULFILMENT_DATA } from '../consts/providers';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import useUpc from '~/modules/upc/composables/useUpc';
import { getSelectedUpcFulfillmentData } from '../utils/productInfoParser';
import useGetItTodayExperiments from './useGetItTodayExperiments';

export const useFulfilmentData = () => injectLocal(FULFILMENT_DATA);

/**
 * Parses fulfillment filter names from a PLP URL hash.
 *
 * @param {string} hash - URL hash containing comma-separated fulfillment filters.
 * @returns {string[]} Parsed fulfillment filter names.
 */
function parseFulfillmentHash(hash) {
  if (!hash) return [];

  try {
    const [options = ''] = decodeURIComponent(hash).replace(/^#/, '').split('/');
    return options.split(',').map((option) => option.trim());
  } catch {
    return [];
  }
}

/**
 * Determines the intended fulfillment method ('ShipIt', 'Pickup', 'SameDay') based on route query parameters or hash.
 *
 * Flow 1: Bag -> PDP
 * - pickInStore=true, sameDay=false -> 'Pickup'
 * - pickInStore=false, sameDay=true -> 'SameDay'
 * - pickInStore=false, sameDay=false -> 'ShipIt'
 *
 * Flow 2: PLP -> PDP
 * - UPC_BOPS_PURCHASABLE -> 'Pickup'
 * - UPC_SDD_PURCHASABLE -> 'SameDay'
 * - UPC_BOPS_PURCHASABLE,UPC_SDD_PURCHASABLE -> 'Pickup' (Pickup takes precedence)
 * - No fulfillment filter/hash -> 'ShipIt'
 *
 * @param {object} route - Nuxt route object
 * @returns {string} 'ShipIt' | 'Pickup' | 'SameDay'
 */
export function getFulfillmentFromUrl(route) {
  // 1. Bag -> PDP flow (Bag query parameters take priority)
  if (route?.query?.pickInStore !== undefined) {
    const pickInStore = String(route.query.pickInStore).toLowerCase();
    const sameDay = String(route.query.sameDay ?? 'false').toLowerCase();
    if (pickInStore === 'true' && sameDay === 'false') {
      return 'Pickup';
    } else if (pickInStore === 'false' && sameDay === 'true') {
      return 'SameDay';
    } else if (pickInStore === 'false' && sameDay === 'false') {
      return 'ShipIt';
    }
  }

  // 2. PLP -> PDP flow (hash values)
  const hash = route?.hash || (typeof window !== 'undefined' ? window.location.hash : '');
  const options = parseFulfillmentHash(hash);
  if (options.includes('UPC_BOPS_PURCHASABLE')) {
    return 'Pickup';
  }
  if (options.includes('UPC_SDD_PURCHASABLE')) {
    return 'SameDay';
  }

  return 'ShipIt';
}

export function useFulfilmentDataProvide() {
  const availabilityStore = useAvailabilityStore();
  const { selectedUpcNumber } = useUpc();
  const { memberProductIdsString } = useMembersOfProduct();
  const { experimentIds = [] } = usePageData();
  const { isChipFulfillmentEnabled } = useGetItTodayExperiments();
  const _customerExperiment = experimentIds.join();
  const currency = Cookie.get(cookies.currency) || 'USD';
  const { isBigTicketItemFlag } = useProductFlags();

  const { zipcode: zipcodeLS, store: storeLocationLS } = getStoreLocation();

  const route = useRoute();
  // Bag/PLP URL selection applies only to enabled Get It Today Chip treatments.
  const getEnabledFulfillmentFromUrl = (currentRoute) => (isChipFulfillmentEnabled.value ? getFulfillmentFromUrl(currentRoute) : 'ShipIt');
  const initialFulfillment = getEnabledFulfillmentFromUrl(route);
  let requestedFulfillment = initialFulfillment;
  let latestAvailabilityRequest = 0;
  let hasCompletedAvailabilityRequest = false;
  let isApplyingUrlSelection = false;
  let isUrlSelectionActive = initialFulfillment !== 'ShipIt';
  const selectedRadioButton = ref('ShipIt');
  const zipCode = ref(getBigTicketZipCookie() || zipcodeLS);

  // Set initial store location from query parameters or saved customer preferences
  const getInitialStore = () => {
    if (route?.query?.bopsLocation) {
      return String(route.query.bopsLocation);
    }

    return storeLocationLS || getSelectedStoreCookie();
  };
  const storeLocation = ref(getInitialStore());

  const isDataLoadNeededOnMount = storeLocation.value || zipCode.value;
  const isDataLoaded = computed(() => availabilityStore.getLoadStatus);

  /**
   * Applies a URL-derived fulfillment selection without treating it as a customer selection.
   *
   * @param {string} selectedOption - Fulfillment option to select.
   * @returns {void}
   */
  function applyUrlSelection(selectedOption) {
    isApplyingUrlSelection = true;
    selectedRadioButton.value = selectedOption;
    isApplyingUrlSelection = false;
  }

  /**
   * Validates the URL-requested option against availability for the selected UPC.
   * Falls back to ShipIt when the requested option cannot be confirmed.
   *
   * @returns {void}
   */
  function validateRequestedFulfillment() {
    if (!isUrlSelectionActive) return;
    if (!hasCompletedAvailabilityRequest || !selectedUpcNumber.value) {
      applyUrlSelection('ShipIt');
      return;
    }

    const { selectedBopsData, selectedSddData } = getSelectedUpcFulfillmentData(availabilityStore.data, selectedUpcNumber.value);
    const isRequestedFulfillmentAvailable =
      requestedFulfillment === 'Pickup'
        ? Boolean(selectedBopsData.availability?.bopsAvailability || selectedBopsData.availability?.bossAvailability)
        : selectedSddData.sddAvailability === true;

    applyUrlSelection(isRequestedFulfillmentAvailable ? requestedFulfillment : 'ShipIt');
  }

  /**
   * Fetches fulfillment availability and validates the latest URL-requested selection.
   *
   * @param {object} params - Parameters forwarded to the availability store.
   * @returns {Promise<void>}
   */
  async function fetchAvailability(params) {
    const requestId = ++latestAvailabilityRequest;
    hasCompletedAvailabilityRequest = false;
    try {
      await availabilityStore.fetchAvailability(params);
    } catch {
      return;
    }

    if (requestId !== latestAvailabilityRequest) return;
    hasCompletedAvailabilityRequest = true;
    validateRequestedFulfillment();
  }

  function _fetchAvailability({ detail = { newStore: { locationNumber: '' } } } = {}) {
    const { newStore } = detail;
    storeLocation.value = newStore.locationNumber;

    fetchAvailability({
      productId: memberProductIdsString.value,
      storeLocation: newStore.locationNumber,
      zipCode: zipCode.value,
      _customerExperiment,
      currency,
      isStoreUpdated: true,
      isBigTicketItemFlag,
    });
  }

  onMounted(async () => {
    navigationStoreChangeListener(_fetchAvailability);
    pageStoreChangeListener(_fetchAvailability);

    // Update fulfillment selection from URL on client side (as route.hash is only populated on client)
    const clientHash = route?.hash || window.location.hash;
    const clientFulfillment = getEnabledFulfillmentFromUrl({
      query: route?.query,
      hash: clientHash,
    });
    if (clientFulfillment !== requestedFulfillment) {
      requestedFulfillment = clientFulfillment;
      isUrlSelectionActive = clientFulfillment !== 'ShipIt';
    }
    validateRequestedFulfillment();

    if (!storeLocation.value && !zipCode.value) return;
    await fetchAvailability({
      productId: memberProductIdsString.value,
      storeLocation: storeLocation.value,
      zipCode: zipCode.value,
      _customerExperiment,
      currency,
      isBigTicketItemFlag,
    });
  });

  onUnmounted(() => {
    removeStoreChangeListener(STORE_CHANGED_ON_HEADER, _fetchAvailability);
    removeStoreChangeListener(STORE_CHANGED_ON_PAGE, _fetchAvailability);
  });

  watch(zipCode, (newValue) => {
    fetchAvailability({
      productId: memberProductIdsString.value,
      storeLocation: storeLocation.value,
      zipCode: newValue,
      _customerExperiment,
      currency,
      isZipCodeUpdated: true,
      isBigTicketItemFlag,
    });
  });

  // Dynamic reset of fulfillment-specific error state
  const fulfillmentError = ref(null);

  watch(selectedUpcNumber, () => {
    validateRequestedFulfillment();
    fulfillmentError.value = null; // Clear error on variant change
  });

  watch(
    selectedRadioButton,
    () => {
      if (!isApplyingUrlSelection) isUrlSelectionActive = false;
      fulfillmentError.value = null; // Clear error on chip selection change
    },
    { flush: 'sync' },
  );

  provideLocal(FULFILMENT_DATA, {
    zipCode,
    storeLocation,
    selectedRadioButton,
    isDataLoadNeededOnMount,
    isDataLoaded,
    fulfillmentError,
  });
}
